import {
  ContextMenuActionRow,
  ContextMenuActionRowItem,
  ContextMenuButtonItem,
  ContextMenuGroup,
  ContextMenuGroupLabel,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubPopup,
  ContextMenuSubTrigger,
  MenuActionRow,
  MenuActionRowItem,
  MenuButtonItem,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
} from "@salestrak/ui";
import type * as React from "react";

import type { MenuPopupWidth, Omittable } from "@salestrak/ui";

/**
 * The menu parts an entity item set composes. Base UI ships a component per
 * menu kind (dropdown Menu vs right-click ContextMenu) with mirrored APIs, so
 * a shared item set takes the part components from its host instead of picking
 * a primitive itself. One entity's actions (an issue's, a workflow's) then
 * serve every surface — a kebab dropdown, a row right-click, a breadcrumb —
 * from a single roster.
 */
export type MenuParts = Omittable<{
  Item: React.ComponentType<{
    onClick?: React.MouseEventHandler<HTMLElement>;
    disabled?: boolean;
    /** Why a `disabled` row is unavailable — the row keeps its seat and
     *  says so in a tooltip (rows are never hidden by role). */
    disabledReason?: string;
    variant?: "default" | "destructive";
    children?: React.ReactNode;
  }>;
  Separator: React.ComponentType<object>;
  /** A labelled run of rows — stages under their category, say. */
  Group: React.ComponentType<{ children?: React.ReactNode }>;
  GroupLabel: React.ComponentType<{ children?: React.ReactNode }>;
  Sub: React.ComponentType<{ children?: React.ReactNode }>;
  SubTrigger: React.ComponentType<{
    disabled?: boolean;
    disabledReason?: string;
    children?: React.ReactNode;
  }>;
  SubPopup: React.ComponentType<{
    children?: React.ReactNode;
    /** `none` lets full-bleed content (an entity selector) own its edges. */
    padding?: "none" | "2xs";
    /** Width floor by menu kind — a submenu carrying a search field takes
     *  `panel` (280, the selector rung), so a searchable submenu is never a
     *  shrink-wrapped sliver. See `MenuPopupWidth`. */
    width?: MenuPopupWidth;
    /** The phone sheet's caption — the trigger row's label, which the
     *  stacked sheet has just covered. See `MenuSubPopup`. */
    sheetTitle?: React.ReactNode;
  }>;
  /** The joined quick-action strip heading a menu, and its segment item. */
  ActionRow: React.ComponentType<{
    children?: React.ReactNode;
  }>;
  ActionRowItem: React.ComponentType<{
    onClick?: React.MouseEventHandler<HTMLElement>;
    disabled?: boolean;
    disabledReason?: string;
    children?: React.ReactNode;
  }>;
  /** The one action a menu promotes above its rows: a full-width themed
   *  primary button at the menu's foot, still a real menu item. */
  ButtonItem: React.ComponentType<{
    onClick?: React.MouseEventHandler<HTMLElement>;
    disabled?: boolean;
    children?: React.ReactNode;
  }>;
}>;

export const DROPDOWN_MENU_PARTS: MenuParts = {
  Item: MenuItem,
  Separator: MenuSeparator,
  Group: MenuGroup,
  GroupLabel: MenuGroupLabel,
  Sub: MenuSub,
  SubTrigger: MenuSubTrigger,
  SubPopup: MenuSubPopup,
  ActionRow: MenuActionRow,
  ActionRowItem: MenuActionRowItem,
  ButtonItem: MenuButtonItem,
};

export const CONTEXT_MENU_PARTS: MenuParts = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Group: ContextMenuGroup,
  GroupLabel: ContextMenuGroupLabel,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubPopup: ContextMenuSubPopup,
  ActionRow: ContextMenuActionRow,
  ActionRowItem: ContextMenuActionRowItem,
  ButtonItem: ContextMenuButtonItem,
};
